// ক্যাটাগরি (taxonomy) — আসল তথ্য ডাটাবেসের taxonomies/terms টেবিলে (supabase/migrations/0005), অ্যাডমিন
// /admin/categories থেকে বদলায়। এই ফাইলে:
//   ১) FALLBACK_TAXONOMIES — প্রথম লোডে/অফলাইনে/DB পাওয়া না গেলে ব্যবহারের তালিকা (migration 0005-এর seed-এর হুবহু কপি)
//   ২) ট্যাগ নিয়ে pure helper। ট্যাগ = "key:value" স্ট্রিং, যেমন "class:class-6"; পেপারে থাকে term_values অ্যারেতে।

export const tagOf = (key, value) => `${key}:${value}`;

export function parseTag(tag) {
  const s = String(tag ?? "");
  const i = s.indexOf(":");
  return i < 1 ? null : { key: s.slice(0, i), value: s.slice(i + 1) };
}

const term = (value, label_bn, label_en) => ({ value, label_bn, label_en });
const taxonomy = (key, label_bn, label_en, multi, sort, terms) => ({
  key,
  label_bn,
  label_en,
  multi,
  required: false,
  sort,
  active: true,
  terms: terms.map((t, i) => ({ ...t, sort: (i + 1) * 10, active: true })),
});

export const FALLBACK_TAXONOMIES = [
  taxonomy("class", "শ্রেণি", "Class", false, 10, [
    term("class-6", "ষষ্ঠ শ্রেণি", "Class 6"),
    term("class-7", "সপ্তম শ্রেণি", "Class 7"),
    term("class-8", "অষ্টম শ্রেণি", "Class 8"),
    term("class-9", "নবম শ্রেণি", "Class 9"),
    term("class-10", "দশম শ্রেণি", "Class 10"),
    term("ssc-hsc", "এসএসসি/এইচএসসি", "SSC/HSC"),
    term("ibtedayi", "ইবতেদায়ী", "Ibtedayi"),
    term("dakhil", "দাখিল", "Dakhil"),
    term("alim", "আলিম", "Alim"),
  ]),
  taxonomy("subject", "বিষয়", "Subject", false, 20, [
    term("bangla", "বাংলা", "Bangla"),
    term("english", "English", "English"),
    term("math", "গণিত", "Mathematics"),
    term("science", "বিজ্ঞান", "Science"),
    term("religion-arabic", "ধর্ম / আরবি", "Religion / Arabic"),
    term("social-science", "সমাজবিজ্ঞান", "Social science"),
    term("ict", "আইসিটি", "ICT"),
    term("other", "অন্যান্য", "Other"),
  ]),
  taxonomy("exam_type", "পরীক্ষার ধরন", "Exam type", false, 30, [
    term("weekly", "সাপ্তাহিক পরীক্ষা", "Weekly test"),
    term("model-test", "মডেল টেস্ট", "Model test"),
    term("half-yearly", "অর্ধ-বার্ষিক", "Half-yearly"),
    term("annual", "বার্ষিক পরীক্ষা", "Annual"),
    term("admission", "ভর্তি পরীক্ষা", "Admission test"),
  ]),
  taxonomy("medium", "মাধ্যম", "Medium", false, 40, [
    term("bn", "বাংলা ভার্সন", "Bangla version"),
    term("en", "English version", "English version"),
    term("ar", "আরবি", "Arabic"),
  ]),
  taxonomy("question_type", "প্রশ্নের ধরন", "Question type", true, 50, [
    term("mcq", "বহুনির্বাচনি (MCQ)", "MCQ"),
    term("short", "সংক্ষিপ্ত প্রশ্ন", "Short question"),
    term("long", "রচনামূলক / বড় প্রশ্ন", "Long question"),
    term("creative", "সৃজনশীল", "Creative"),
    term("fill-blank", "শূন্যস্থান পূরণ", "Fill in the blanks"),
    term("true-false", "সত্য / মিথ্যা", "True / False"),
    term("matching", "মিলকরণ", "Matching"),
  ]),
];

// ফিল্টার/পিকারে শুধু সক্রিয় ক্যাটাগরির সক্রিয় মান (অন্তত একটা মান আছে এমন)
export function activeTaxonomies(taxonomies) {
  return taxonomies
    .filter((x) => x.active)
    .map((x) => ({ ...x, terms: x.terms.filter((m) => m.active) }))
    .filter((x) => x.terms.length > 0);
}

// লেবেল খোঁজা নিষ্ক্রিয় মান সহ সবটায় — পুরনো পেপারের ট্যাগেরও নাম দেখাতে হয়। না পেলে মানটাই।
export function labelOf(taxonomies, key, value) {
  return taxonomies.find((x) => x.key === key)?.terms.find((m) => m.value === value)?.label_bn ?? value;
}

// পেপারের ট্যাগ। term_values না থাকলে (পুরনো row) পুরনো কলাম থেকে।
export function tagsOf(paper) {
  if (Array.isArray(paper?.term_values) && paper.term_values.length > 0) return paper.term_values;
  return [
    paper?.class_level && tagOf("class", paper.class_level),
    paper?.subject && tagOf("subject", paper.subject),
    paper?.exam_type && tagOf("exam_type", paper.exam_type),
    paper?.medium && tagOf("medium", paper.medium),
  ].filter(Boolean);
}

// ট্যাগগুলো ক্যাটাগরি ও মানের ক্রম অনুযায়ী [{ key, value, label }]
export function describeTags(taxonomies, paper) {
  const position = (key, value) => {
    const xi = taxonomies.findIndex((x) => x.key === key);
    const ti = xi < 0 ? -1 : taxonomies[xi].terms.findIndex((m) => m.value === value);
    return [xi < 0 ? 999 : xi, ti < 0 ? 999 : ti];
  };
  return tagsOf(paper)
    .map(parseTag)
    .filter(Boolean)
    .map(({ key, value }) => ({ key, value, label: labelOf(taxonomies, key, value), pos: position(key, value) }))
    .sort((a, b) => a.pos[0] - b.pos[0] || a.pos[1] - b.pos[1])
    .map(({ pos: _pos, ...rest }) => rest);
}
